import React, { useCallback } from 'react';
import memoize from 'lodash/memoize';
import Lozenge from '@atlaskit/lozenge';
import { Box, Text, Flex, xcss } from '@atlaskit/primitives';
import { DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DynamicResultTable, FormattedDate } from '@css-xp/base';
import { emptyStateProp, errorStateProp, TableHeading, emptyStatePropForUserHistoryTable } from '@css-xp/ppc-common';
import { UserHistoryGraph } from './user-history-graph';
import { useFetchUsageHistoryGraphQuery } from './use-fetch-usage-history-graph-query';
import './product-usage-table.scss';

interface Props {
  id: string;
  transactionAccountId: string;
  tableHeading: string;
  entitlementNextBillingDate?: string | null;
  isDesignIteration1Enabled?: boolean;
  isNewHWBGraphChangesEnabled?: boolean;
  openFeedbackWindow?: () => void;
}

interface UsagePeriodTime {
  startTime: string;
  endTime: string;
}
interface UsageGraphHistory {
  highWaterMarkUsage?: number;
  periodTimestamp: string;
  latestUsage: number;
  maxUsage: number;
  usersAdded?: number;
  usersRemoved?: number;
}

export const ProductUsageGraphHistoryTable = ({
  id,
  transactionAccountId,
  tableHeading,
  entitlementNextBillingDate,
  isDesignIteration1Enabled,
  isNewHWBGraphChangesEnabled,
  openFeedbackWindow,
}: Props) => {
  const { loading: usageGraphLoading, data, error } = useFetchUsageHistoryGraphQuery({
    entitlementId: id,
    transactionAccountId: transactionAccountId,
  });

  const usagePeriodTime: UsagePeriodTime = data?.entitlementDetails?.usageAggregation?.period;
  const usageAggregationData: UsageGraphHistory[] = data?.entitlementDetails?.usageAggregation?.results ?? [];

  const getRows = () => {
    return usageAggregationData.map((rowList) => {
      const maxBillableUsers = rowList?.highWaterMarkUsage ? rowList?.highWaterMarkUsage : rowList?.latestUsage;
      return {
        key: rowList.periodTimestamp.toString(),
        cells: [
          {
            content: <FormattedDate date={rowList.periodTimestamp.toString()} format={DATE_TIME_FORMAT} />,
          },
          {
            content: rowList?.usersAdded ? <span className="user-change-add">{`+ ${rowList?.usersAdded}`}</span> : '',
          },
          {
            content: rowList?.usersRemoved ? (
              <span className="user-change-remove">{`- ${rowList?.usersRemoved}`}</span>
            ) : (
              ''
            ),
          },
          {
            content: rowList?.maxUsage?.toString(),
          },
          {
            content: maxBillableUsers.toString(),
          },
        ],
      };
    });
  };

  const getHead = memoize(() => {
    return {
      cells: [
        {
          content: 'Last activity',
          width: 40,
        },
        {
          content: 'Users added',
          width: 15,
        },
        {
          content: `Users removed`,
          width: 15,
        },
        {
          content: `Seats used`,
          width: 15,
        },
        {
          content: `Total seats`,
          width: 15,
        },
      ],
    };
  });

  const renderUsageGraph = useCallback(() => {
    if (!usageGraphLoading && usageAggregationData.length) {
      const usageGraphPoints = usageAggregationData.map((point) => ({
        timestamp: parseInt(point.periodTimestamp),
        latestUsage: point.latestUsage,
        maximumUsage: point.maxUsage,
      }));
      const periodStartTime = Number(usagePeriodTime.startTime);
      const periodEndTime = Number(usagePeriodTime.endTime);
      const nextBillingDate = entitlementNextBillingDate
        ? new Date(entitlementNextBillingDate).valueOf()
        : periodEndTime;
      const graphPeriod = {
        start: periodStartTime,
        end: nextBillingDate,
      };

      const interpolationPeriod = { start: periodStartTime, end: periodEndTime };
      return (
        <>
          {isNewHWBGraphChangesEnabled && (
            <Flex
              xcss={xcss({
                backgroundColor: 'elevation.surface.sunken',
                padding: 'space.100',
                border: '1px solid elevation.surface.overlay.hovered',
              })}
              gap="space.100"
              alignItems="center"
            >
              <Lozenge appearance="inprogress" isBold>
                Beta
              </Lozenge>
              <Text size="small">
                This feature is new. <a onClick={openFeedbackWindow}>Tell us what you think</a>
              </Text>
            </Flex>
          )}
          <UserHistoryGraph
            period={graphPeriod}
            points={usageGraphPoints}
            usageLabel="Users"
            interpolationPeriod={interpolationPeriod}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
            isNewHWBGraphChangesEnabled={isNewHWBGraphChangesEnabled}
          />
        </>
      );
    }
    return <></>;
  }, [usageAggregationData, usageGraphLoading, isDesignIteration1Enabled, isNewHWBGraphChangesEnabled]);

  return (
    <DynamicResultTable
      isLoading={usageGraphLoading}
      testId="product-usage-graph-history-table"
      beforeTable={isDesignIteration1Enabled ? renderUsageGraph() : <TableHeading>{tableHeading}</TableHeading>}
      beforeTableHead={
        isDesignIteration1Enabled ? (
          <Box paddingBlockEnd="space.300">
            <Text weight="semibold">{tableHeading}</Text>
          </Box>
        ) : (
          renderUsageGraph()
        )
      }
      head={getHead()}
      rows={getRows()}
      emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForUserHistoryTable : emptyStateProp}
      totalCount={usageAggregationData.length}
      errorStateProps={errorStateProp}
      error={error?.message}
      hideHeadOnEmptyState={isDesignIteration1Enabled}
    />
  );
};
